import { Box, Grid, Typography } from "@mui/material";
import { Dayjs } from "dayjs";
import { useEffect, useRef,  useState } from "react";
import { T } from "react-translator-component";
import DateSelectDialog from "../DateSelectDialog";
import apiServices from "../../services/apiServices";
import { toDisplayedDate, toDisplayedTerm } from "../../util/statistics";
import { formatNumber } from "../../util/util";
import Loading from "../Loading";
import Section from "../Section";
import Divider from "./Divider"
import AggregationButton from "./AggregationButton";
import Term, { TermRef } from "../Term"
import { styles as baseStyle } from "./styles"

interface Props {
  baseDate: Dayjs,
  initialized: () => void,
  handleError: (error: any) => void,
}

const Summary: React.FC<Props> = ({ baseDate, initialized, handleError }: Props) => {
  /* 入力データ用 */
  const [targetDate, setTargetDate] = useState<Dayjs>(baseDate);
  const termRef = useRef<TermRef>(null);

  /* 出力データ用 */
  const [totalData, setTotalData] = useState<any>();
  const [termData, setTermData] = useState<any>();

  /* 表示制御用 */
  const [loadingTotalData, setLoadingTotalData] = useState<boolean>(false);
  const [loadingTermData, setLoadingTermData] = useState<boolean>(false);

  useEffect(() => {
    initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initialize = async () => {
    try {
      await Promise.all([
        fetchTotalData(targetDate),
        fetchTermData(baseDate, baseDate)
      ]);
    } catch (error: any) {
      handleError(error);
    } finally {
      initialized();
    }
  }

  const fetchTotalData = async (_targetDate: Dayjs) => {
    const data = await apiServices.getStatisticsSummaryTotal(_targetDate);
    setTotalData(data);
  }

  const fetchTermData = async (_startDate: Dayjs, _endDate: Dayjs) => {
    const data = await apiServices.getStatisticsSummaryTerm(_startDate, _endDate);
    setTermData(data);
  }

  /* UI 操作関連 */

  const submitTotal = async () => {
    try {
      setLoadingTotalData(true);
      await fetchTotalData(targetDate);
    } catch (error: any) {
      handleError(error);
    } finally {
      setLoadingTotalData(false);
    }
    return false
  }

  const submitTerm = async () => {
    if (!termRef || !termRef.current) {
      return false
    }

    try {
      setLoadingTermData(true);
      const term = termRef.current.getTerm();
      await fetchTermData(term.startDate, term.endDate);
    } catch (error: any) {
      handleError(error);
    } finally {
      setLoadingTermData(false);
    }
    return false
  }

  /* DOM */
  const dataBox = (props: {title: string, children: React.ReactElement}) => (
    <>
      <Typography sx={baseStyle.dataTitle}>{ props.title }</Typography>
      { props.children }
    </>
  );

  return (
    <Section title={T('mng_summary')}>
      <Grid container sx={styles.summary}>
        <Grid item xs={4} sx={styles.conditions}>
          <Typography>{T('mng_target_date')}</Typography>
          <DateSelectDialog
            inputedDate={targetDate}
            maxDate={baseDate}
            onChange={(date)=>{setTargetDate(date)}}
          />
          <AggregationButton
            onSubmit={submitTotal}
            validate={async () => true}
          />
        </Grid>
        <Grid item xs={8} sx={styles.data}>
          {dataBox({
            title: `${T('mng_registration')}    ${ toDisplayedDate(totalData?.date) }`,
            children: (
              <Grid container component={"dl"}>
                <Loading visible={loadingTotalData} />
                <Grid item component={"dt"} xs={7}>{T('mng_summary_registration_total_users')}</Grid>
                <Grid item component={"dd"} xs={5}>{ formatNumber(totalData?.registration?.total_users)}</Grid>
                <Grid item component={"dt"} xs={7}>{T('mng_summary_registration_total_member_groups')}</Grid>
                <Grid item component={"dd"} xs={5}>{ formatNumber(totalData?.registration?.total_member_groups)}</Grid>
                <Grid item component={"dt"} xs={7}>{T('mng_summary_registration_registered_personal_basic_info')}</Grid>
                <Grid item component={"dd"} xs={5}>{ formatNumber(totalData?.registration?.users_registered_personal_basic_info)}</Grid>
              </Grid>
            )
          })}
        </Grid>

        <Divider />

        <Grid item xs={4} sx={styles.conditions}>
          <Typography>{T('mng_term')}</Typography>
          <Term
            baseDate={baseDate}
            vertical={true}
            ref={termRef}
          />
          <Box sx={styles.buttonContainer}>
            <AggregationButton
              onSubmit={submitTerm}
              validate={() => Promise.resolve(termRef.current?.validate() ?? false)}
            />
          </Box>
        </Grid>
        <Grid item xs={8} sx={styles.data}>
          {dataBox({
            title: `${T('mng_registration')}    ${ toDisplayedTerm(termData?.term?.start_date, termData?.term?.end_date) }`,
            children: (
              <Grid container component={"dl"}>
                <Loading visible={loadingTermData} />
                <Grid item component={"dt"} xs={7}>{T('mng_summary_registration_new_users')}</Grid>
                <Grid item component={"dd"} xs={5}>{ formatNumber(termData?.registration?.new_users)}</Grid>
                <Grid item component={"dt"} xs={7}>{T('mng_summary_registration_new_member_groups')}</Grid>
                <Grid item component={"dd"} xs={5}>{ formatNumber(termData?.registration?.new_member_groups)}</Grid>
              </Grid>
            )
          })}
          {dataBox({
            title: `${T('mng_utilization')}    ${ toDisplayedTerm(termData?.term?.start_date, termData?.term?.end_date) }`,
            children: (
              <Grid container component={"dl"}>
                <Loading visible={loadingTermData} />
                <Grid item component={"dt"} xs={7}>{T('mng_summary_utilization_users_posts')}</Grid>
                <Grid item component={"dd"} xs={5}>{ formatNumber(termData?.utilization?.users_posts) }</Grid>
                <Grid item component={"dt"} xs={7}>{T('mng_summary_utilization_posts_in_group')}</Grid>
                <Grid item component={"dd"} xs={5}>{ formatNumber(termData?.utilization?.posts_in_group) }</Grid>
                <Grid item component={"dt"} xs={7}>{T('mng_summary_utilization_private_posts_in_group')}</Grid>
                <Grid item component={"dd"} xs={5}>{ formatNumber(termData?.utilization?.private_posts_in_group) }</Grid>
                <Grid item component={"dt"} xs={7}>{T('mng_summary_utilization_groups_with_many_posts')}</Grid>
                <Grid item component={"dd"} xs={5}>{ formatNumber(termData?.utilization?.groups_with_many_posts) }</Grid>
              </Grid>
            )
          })}
        </Grid>
      </Grid>
    </Section>
  )
}
export default Summary

const styles = {
  summary: {
    "& dl": {
      borderTop: "1px solid #DCDCDC",
      borderRight: "1px solid #DCDCDC",
      position: "relative"
    },
    "& dt": {
      borderLeft: "1px solid #DCDCDC",
      borderBottom: "1px solid #DCDCDC",
      fontWeight: "normal",
      padding: 1
    },
    "& dd": {
      borderLeft: "1px solid #DCDCDC",
      borderBottom: "1px solid #DCDCDC",
      padding: 1,
      textAlign: "right"
    }
  },
  conditions: {
    padding: 1
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "flex-end",
    paddingTop: 1
  },
  data: {
    padding: 2
  }
}
